import { flatten, forEach, isEmpty, map, pipe } from 'ramda'

import {
  ReportOrdersByClassesData,
  CountInfoType,
  Orgs,
  ByClasses,
  TotalItem,
  numberOfFeedingArrange,
  OrderByClassesDetailsGroup,
  OrgByCustomersAndFunds,
  AllowancesByClasses,
} from 'app/dataTypes/reports/data/common/ordersByClasses'

import SimpleDate from 'app/common/classes/simple-date'

import { delimiterToProp, DELIMITER } from './delimiterToProp'
import { ByClassesData, Classes } from './data-api-by-classes.type'
import { ByFundsData } from './data-api-by-funds.type'
import { isByClassesData, isByCustomersAndFundsData, isByFundsData } from './data-api-check'
import { convertUnperformed } from './data-unperformed'
import { AllowancesByClassesApi, ByCustomersAndFundsData, CountInfoTypeApi } from './data-api-by-customers-and-funds.type'


export type ApiResultOrderByClasses = ByClassesData | ByFundsData | ByCustomersAndFundsData

// eslint-disable-next-line @typescript-eslint/default-param-last
const convertByClasses = ({
  classes_1_4: _1_4 = null,
  classes_5_9: _5_9 = null,
  classes_10_11: _10_11 = null,
  classes_total: total = null,
}: Classes = {} as Classes, key: string): ByClasses => ({ key, _1_4, _5_9, _10_11, total })

const convertTotal = (val: number | null | undefined, key: string) => ({
  key,
  value: typeof val === 'number' ? val : null,
})

const feedingApiKeys = numberOfFeedingArrange // remap if api keys different

const createOrgs = (data: ByClassesData): { orgs?: Orgs } => {
  const { details } = data
  const { schools, schoolyear_from_year: fromYear, total } = details
  if (
    schools
    && fromYear
    && total
    && !isEmpty(schools)
    && !isEmpty(total)
  ) {
    const shiftsClassesSet = new Set<number>()
    const shiftsDayCaresSet = new Set<number>()

    forEach(({ classes_shifts: classes = [], day_cares_shifts: dayCares = [] }) => {
      forEach(key => shiftsClassesSet.add(key), classes)
      forEach(key => shiftsDayCaresSet.add(key), dayCares)
    }, schools)

    const shiftsClasses = Array.from(shiftsClassesSet).sort()
    const shiftsDayCares = Array.from(shiftsDayCaresSet).sort()

    if (isEmpty(shiftsClasses) && isEmpty(shiftsDayCares)) {
      throw new Error('Ошибка данных')
    }

    const createRow = (dataLocal: ByClassesData['details']['schools'][number]) => delimiterToProp([
      convertByClasses(dataLocal.planned_classes_total, 'planned_classes'),

      DELIMITER,

      ...shiftsDayCares.map(shift => convertByClasses(
        dataLocal[`planned_day_cares_shift_${shift}`],
        `planned_daycares_${shift}`,
      )),

      DELIMITER,

      convertByClasses(dataLocal.with_orders, 'orders'),

      DELIMITER,

      ...flatten(shiftsClasses.map(shift => ([
        ...feedingApiKeys.map(feedKey => (
          convertByClasses(
            dataLocal[`with_classes_orders_subsidies_shift_${shift}_${feedKey}`],
            `subsidies_classes_${shift}_${feedKey}`,
          )
        )),
        DELIMITER,
      ]))),

      DELIMITER,

      ...flatten(shiftsDayCares.map(shift => ([
        ...feedingApiKeys.map(feedKey => (
          convertByClasses(
            dataLocal[`with_day_cares_orders_subsidies_shift_${shift}_${feedKey}`],
            `subsidies_daycares_${shift}_${feedKey}`,
          )
        )),
        DELIMITER,
      ]))),

      DELIMITER,

      ...flatten(shiftsClasses.map(shift => ([
        feedingApiKeys.map(feedKey => (
          convertByClasses(
            dataLocal[`with_orders_expenses_shift_${shift}_${feedKey}`],
            `expensess_${shift}_${feedKey}`,
          )
        )),
        DELIMITER,
      ]))),
    ])

    const items = map((school) => {
      const { school_id: id, school_title: title } = school
      return ({
        id: String(id),
        title,
        numberOf: createRow(school),
      })
    }, schools)

    return {
      orgs: {
        items,
        total: delimiterToProp<TotalItem>([
          convertTotal(total.planned_classes_total, 'planned_classes'),

          DELIMITER,

          ...shiftsDayCares.map(shift => convertTotal(
            total[`planned_day_cares_shift_${shift}`],
            `planned_daycares_${shift}`,
          )),

          DELIMITER,

          convertTotal(total.with_orders, 'orders'),

          DELIMITER,

          ...flatten(shiftsClasses.map(shift => ([
            ...feedingApiKeys.map(feedKey => (
              convertTotal(
                total[`with_classes_orders_subsidies_shift_${shift}_${feedKey}`],
                `subsidies_classes_${shift}_${feedKey}`,
              )
            )),
            DELIMITER,
          ]))),

          ...flatten(shiftsDayCares.map(shift => ([
            ...feedingApiKeys.map(feedKey => (
              convertTotal(
                total[`with_day_cares_orders_subsidies_shift_${shift}_${feedKey}`],
                `subsidies_daycares_${shift}_${feedKey}`,
              )
            )),
            DELIMITER,
          ]))),

          ...flatten(shiftsClasses.map(shift => ([
            ...feedingApiKeys.map(feedKey => (
              convertTotal(
                total[`with_orders_expenses_shift_${shift}_${feedKey}`],
                `expensess_${shift}_${feedKey}`,
              )
            )),
            DELIMITER,
          ]))),
        ]),
        plannedDate: new SimpleDate(`${fromYear}-09-01`, 'YYYY-MM-DD'),
        shiftsClasses,
        shiftsDayCares,
      },
    }
  }
  return {}
}

const toNumber = pipe(String, parseFloat)

const convertCountInfo = (countInfo: CountInfoTypeApi): CountInfoType => {
  const {
    expenses_count: parentsExpensesCount,
    allowances,
    total_count: totalCount
  } = countInfo
  return {
    parentsExpensesCount,
    allowances,
    totalCount
  }
}

const convertAllowances = (allowances: AllowancesByClassesApi): AllowancesByClasses => {
  const {
    count_1_4: countFrom1To4,
    count_5_11: countFrom5To11,
  } = allowances
  return {
    countFrom1To4,
    countFrom5To11,
  }
}


export const convertResultOrdersByClasses = (apiData: ApiResultOrderByClasses): ReportOrdersByClassesData => {
  if (isByClassesData(apiData)) {
    const { summary, unperformed: unperformedDto } = apiData

    const {
      with_orders: withOrders,
      with_orders_expenses: withOrdersExpenses,
      with_orders_subsidies: withOrdersSubsidies,
    } = map<Record<string, number | string>, Record<string, number>>(toNumber, summary)

    const unperformed = convertUnperformed(unperformedDto)

    return {
      detailsGroup: OrderByClassesDetailsGroup.ByClasses,
      summary: {
        withOrders,
        withOrdersExpenses,
        withOrdersSubsidies,
      },
      unperformed,
      ...createOrgs(apiData),
    }
  }

  if (isByFundsData(apiData)) {
    const { allowances, schools, total, unperformed: unperformedDto } = apiData
    const {
      expenses_count: totalParentsExpensesCount,
      allowances_1_11: totalBudgetExpencesFrom1To11Count,
      allowances_1_11_count: totalBudgetExpencesFrom1To11CountTotal,
      common_order_count: totalBudgetExpencesFrom1To4Count,
    } = total

    const unperformed = convertUnperformed(unperformedDto)

    const res = {
      detailsGroup: OrderByClassesDetailsGroup.ByFunds as const,
      allowanceIDs: allowances,
      orgs: schools.map(({
        school_id: orgId,
        school_title: orgTitle,
        expenses_count: parentsExpensesCount,
        allowances_1_11: allowances111,
        allowances_1_11_count: budgetExpencesFrom1To11CountTotal,
        common_order_count: budgetExpencesFrom1To4Count,
        total: totalCount,
      }) => {
        return {
          orgID: String(orgId),
          orgTitle,
          parentsExpensesCount,
          budgetExpencesFrom1To11Count: allowances.map(
            allowanceID => allowances111[allowanceID] || 0,
          ),
          budgetExpencesFrom1To11CountTotal,
          budgetExpencesFrom1To4Count,
          totalCount,
        }
      }).sort((a, b) => a.orgTitle.localeCompare(b.orgTitle)),
      totals: {
        parentsExpensesCount: totalParentsExpensesCount,
        budgetExpencesFrom1To11Count: allowances.map(
          allowanceID => totalBudgetExpencesFrom1To11Count[allowanceID] || 0,
        ),
        budgetExpencesFrom1To11CountTotal: totalBudgetExpencesFrom1To11CountTotal,
        budgetExpencesFrom1To4Count: totalBudgetExpencesFrom1To4Count,
        totalCount: schools.reduce((acc, val) => acc + val.total, 0),
      },
      unperformed,
    }
    return res
  }

  if (isByCustomersAndFundsData(apiData)) {
    const { allowances, schools, total, unperformed: unperformedDto } = apiData
    const {
      count_1_4: totalFrom1To4count,
      count_5_11: totalFrom5To11count,
    } = total

    const unperformed = convertUnperformed(unperformedDto)

    const res = {
      detailsGroup: OrderByClassesDetailsGroup.ByCustomersAndFunds as const,
      allowances: convertAllowances(allowances),
      orgs: schools.map(({
        school_id: orgId,
        school_title: orgTitle,
        count_1_4: countFrom1To4,
        count_5_11: countFrom5To11,
      }) => {
        return {
          orgID: String(orgId),
          orgTitle,
          countFrom1To4: convertCountInfo(countFrom1To4),
          countFrom5To11: convertCountInfo(countFrom5To11),
        } as OrgByCustomersAndFunds
      }).sort((a, b) => a.orgTitle.localeCompare(b.orgTitle)),
      totals: {
        countFrom1To4: convertCountInfo(totalFrom1To4count),
        countFrom5To11: convertCountInfo(totalFrom5To11count),
      },
      unperformed,
    }
    return res
  }

  const exhaustiveCheck: never = apiData
  return exhaustiveCheck
}
